import React, { useCallback, useRef } from 'react';
import SelectBox from 'devextreme-react/select-box';
import Button from 'devextreme-react/button';
import TreeList, {
    Column,
    FilterRow,
    Scrolling,
    HeaderFilter,
    Selection,
    StateStoring
} from 'devextreme-react/tree-list';
import type { TemplateSyncGrantResult } from '../../../dtos/TemplateSyncGrantResult';
import type { TemplateSyncRunResult } from '../../../dtos/TemplateSyncRunResult';
import { TemplateSyncTreeRow } from '../hooks/useTemplateSyncController';
import { getPhaseStatusInfo } from '../../shared/Utils';

interface TemplateSyncViewProps {
    isSuper: boolean;
    modules: { id: number; name: string }[];
    selectedModuleId: number | null;
    setSelectedModuleId: (id: number | null) => void;
    results: TemplateSyncGrantResult[];
    treeRows: TemplateSyncTreeRow[];
    selectedRowKeys: string[];
    setSelectedRowKeys: (keys: string[]) => void;
    selectedGrantCount: number;
    loading: boolean;
    syncing: boolean;
    syncResult: TemplateSyncRunResult | null;
    sync: () => void;
}

const TypeTag: React.FC<{ label: string; tone?: 'new' | 'different' | 'neutral' }> = ({ label, tone = 'neutral' }) => {
    const palette = tone === 'new'
        ? { background: '#28a745', color: 'white' }
        : tone === 'different'
            ? { background: '#fd7e14', color: 'white' }
            : { background: '#e0e0e0', color: '#555' };
    return (
        <span
            style={{
                display: 'inline-block',
                marginLeft: 6,
                padding: '1px 6px',
                borderRadius: '3px',
                fontSize: '0.85em',
                ...palette
            }}
        >
            {label}
        </span>
    );
};

const DifferenceDiff: React.FC<{ label: string; current: string | null | undefined; template: string | null | undefined }> = ({ label, current, template }) => (
    <div style={{ marginTop: 2, fontSize: '0.85em', color: '#555' }}>
        <strong>{label}:</strong>{' '}
        <span style={{ textDecoration: 'line-through', color: '#a00' }}>{current || <em>(empty)</em>}</span>
        {' → '}
        <span style={{ color: '#1a7f37' }}>{template || <em>(empty)</em>}</span>
    </div>
);

const nameCellRender = (cellData: any) => {
    const row = cellData.data as TemplateSyncTreeRow;
    if (row.rowType === 'grant') {
        const inst = row.institution ? ` — ${row.institution}` : '';
        const parts: string[] = [];
        if (row.missingTaskCount) parts.push(`${row.missingTaskCount} new task(s)`);
        if (row.missingSubtaskCount) parts.push(`${row.missingSubtaskCount} missing subtask(s)`);
        if (row.differentTaskCount) parts.push(`${row.differentTaskCount} task title(s) differ`);
        if (row.differentSubtaskCount) parts.push(`${row.differentSubtaskCount} subtask(s) differ`);
        return (
            <span>
                <strong>{row.grantNumber}</strong>{inst} <span style={{ color: '#888' }}>(FY{row.fy})</span>
                {parts.length > 0 && (
                    <span style={{ marginLeft: 8, color: '#888' }}>{parts.join(', ')}</span>
                )}
            </span>
        );
    }
    if (row.rowType === 'task') {
        const statusInfo = row.isExistingTask && row.currentStatus != null
            ? getPhaseStatusInfo(row.currentStatus, null, row.workflowType ?? null)
            : null;
        return (
            <span>
                {row.name}
                <TypeTag label="Task" />
                {!row.isExistingTask && <TypeTag label="New" tone="new" />}
                {row.isExistingTask && row.titleDiffers && <TypeTag label="Title Differs" tone="different" />}
                {statusInfo && (
                    <span
                        style={{
                            marginLeft: 6,
                            padding: '1px 6px',
                            borderRadius: '3px',
                            background: statusInfo.color,
                            border: statusInfo.border,
                            fontSize: '0.85em'
                        }}
                    >
                        {statusInfo.text}
                    </span>
                )}
                {row.isExistingTask && row.titleDiffers && (
                    <DifferenceDiff label="Title" current={row.currentTitle} template={row.templateTitle} />
                )}
            </span>
        );
    }
    // Subtask
    return (
        <span>
            {row.name}
            <TypeTag label="Subtask" />
            {row.isMissingSubtask && <TypeTag label="New" tone="new" />}
            {!row.isMissingSubtask && row.titleDiffers && <TypeTag label="Title Differs" tone="different" />}
            {!row.isMissingSubtask && row.descriptionDiffers && <TypeTag label="Description Differs" tone="different" />}
            {!row.isMissingSubtask && row.titleDiffers && (
                <DifferenceDiff label="Title" current={row.currentTitle} template={row.templateTitle} />
            )}
            {!row.isMissingSubtask && row.descriptionDiffers && (
                <DifferenceDiff label="Description" current={row.currentDescription} template={row.templateDescription} />
            )}
        </span>
    );
};

const TemplateSyncView: React.FC<TemplateSyncViewProps> = ({
    modules, selectedModuleId, setSelectedModuleId,
    results, treeRows, selectedRowKeys, setSelectedRowKeys, selectedGrantCount,
    loading, syncing, syncResult,
    sync
}) => {

    const onSelectionChanged = useCallback((e: any) => {
        setSelectedRowKeys(e.selectedRowKeys as string[]);
    }, [setSelectedRowKeys]);

    // In-memory state cache so a post-sync re-detect doesn't blow away the
    // user's filters or expanded rows. We strip selectedRowKeys out of the
    // saved state — selection is owned by the controller and re-applied via
    // the selectedRowKeys prop, and letting StateStoring also restore it
    // causes a stale list to override the controller's value.
    const treeStateRef = useRef<any>(null);
    const customLoad = useCallback(() => treeStateRef.current, []);
    const customSave = useCallback((state: any) => {
        if (state) {
            const { selectedRowKeys: _ignored, ...rest } = state;
            treeStateRef.current = rest;
        }
    }, []);

    return (
        <div className="container-fluid" style={{ padding: '20px' }}>
            <h4 style={{ marginBottom: '15px' }}>
                Template Sync
                <span style={{ fontSize: '0.7em', color: '#666', fontWeight: 'normal', marginLeft: '10px' }}>
                    Sync missing tasks/subtasks and updated titles/descriptions from templates to existing grants.
                    Selecting a parent automatically includes all of its children.
                </span>
            </h4>

            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '15px' }}>
                <label><strong>Module:</strong></label>
                <SelectBox
                    dataSource={modules}
                    valueExpr="id"
                    displayExpr="name"
                    value={selectedModuleId}
                    onValueChanged={(e) => setSelectedModuleId(e.value)}
                    placeholder="Select a module"
                    searchEnabled
                    width={300}
                />
                <Button
                    text={`Sync Selected (${selectedGrantCount} grant${selectedGrantCount === 1 ? '' : 's'})`}
                    type="success"
                    onClick={sync}
                    disabled={syncing || selectedGrantCount === 0}
                />
                {loading && <span>Detecting...</span>}
                {syncing && <span>Syncing...</span>}
                {!loading && results.length > 0 && (
                    <span style={{ color: '#666' }}><strong>{results.length}</strong> grant(s) out of sync</span>
                )}
            </div>

            {!loading && results.length > 0 && (
                <>
                    {syncResult && syncResult.errors.length > 0 && (
                        <div style={{ color: 'red', marginBottom: '10px', border: '1px solid #dc3545', padding: '8px', borderRadius: '4px' }}>
                            <strong>Errors:</strong>
                            <ul style={{ margin: '4px 0 0 0' }}>{syncResult.errors.map((e, i) => <li key={i}>{e}</li>)}</ul>
                        </div>
                    )}

                    <TreeList
                        dataSource={treeRows}
                        keyExpr="id"
                        parentIdExpr="parentId"
                        showBorders={true}
                        showColumnLines={false}
                        columnAutoWidth={true}
                        autoExpandAll={false}
                        wordWrapEnabled={true}
                        showRowLines={true}
                        rowAlternationEnabled={true}
                        selectedRowKeys={selectedRowKeys}
                        onSelectionChanged={onSelectionChanged}
                        filterMode="fullBranch"
                        height={window.innerHeight - 280}
                    >
                        <Selection mode="multiple" recursive={true} />
                        <FilterRow visible={true} />
                        <HeaderFilter visible={true} />
                        <Scrolling mode="standard" />
                        <StateStoring enabled={true} type="custom" customLoad={customLoad} customSave={customSave} savingTimeout={0} />
                        <Column dataField="name" caption="Grant / Task / Subtask" cellRender={nameCellRender} minWidth={400} />
                    </TreeList>
                </>
            )}

            {!loading && results.length === 0 && selectedModuleId && (
                <div style={{ color: '#666', padding: '20px 0' }}>
                    {syncResult ? 'All grants are now in sync.' : 'No grants are out of sync for this module.'}
                </div>
            )}
        </div>
    );
};

export default TemplateSyncView;
